import { ICellEditorComp, ICellEditorParams } from 'ag-grid-community';
import { addEventListener, IDisposable } from '@samc/common';
import {
  filterTaskTypePickerRowsBySearch,
  ITaskTypePickerRow,
} from '@/utils/taskTypePickerRows';

interface ISamcDropdownElement extends HTMLElement {
  enableSearch: boolean;
  placeholder?: string;
  value?: string | number | null;
  isOpened: boolean;
}

interface ISamcOptionElement extends HTMLElement {
  value: string | unknown;
  text?: string;
  hasChildren: boolean;
  level: number;
}

export interface TaskTypePickerCellEditorParams extends ICellEditorParams {
  getRows: () => ITaskTypePickerRow[];
  filterRows?: (rows: ITaskTypePickerRow[], search: string) => ITaskTypePickerRow[];
  placeholder?: string;
  getValue?: (data: unknown) => number | undefined | null;
}

export class TaskTypePickerCellEditor implements ICellEditorComp {
  private _gui!: HTMLDivElement;
  private _dropdown!: ISamcDropdownElement;
  private _value: number | undefined | null;
  private _params!: TaskTypePickerCellEditorParams;
  private _searchTerm = '';
  private _disposables: IDisposable[] = [];

  init(params: TaskTypePickerCellEditorParams): void {
    this._params = params;
    this._value = params.getValue?.(params.data) ?? undefined;

    this._gui = document.createElement('div');
    this._gui.style.cssText = 'width:100%;height:100%';

    this._dropdown = document.createElement('samc-dropdown') as ISamcDropdownElement;
    this._dropdown.enableSearch = true;
    this._dropdown.placeholder = params.placeholder ?? 'Select task type...';
    this._dropdown.value =
      this._value !== null && this._value !== undefined && this._value !== 0
        ? String(this._value)
        : undefined;
    this._dropdown.isOpened = true;
    this._dropdown.style.width = '100%';

    this._renderOptions();
    this._gui.appendChild(this._dropdown);

    const onSearch = (e: Event) => {
      this._searchTerm = ((e as CustomEvent<string | null>).detail ?? '').trim();
      this._renderOptions();
    };
    const onValueChanged = (e: Event) => {
      const detail = (e as CustomEvent<string | undefined>).detail;
      if (detail === undefined || detail === null || detail === '') {
        this._value = undefined;
        return;
      }
      if (typeof detail === 'string' && detail.startsWith('group:')) {
        return;
      }
      const id = Number(detail);
      if (!Number.isNaN(id)) {
        this._value = id;
      }
    };
    const onSelectOption = (e: Event) => {
      const option = (e as CustomEvent<{ hasChildren?: boolean; value?: unknown }>).detail;
      if (option === undefined || option === null || option.hasChildren) {
        return;
      }
      let selected: string | null = null;
      if (option.value !== undefined && option.value !== null) {
        selected = String(option.value);
      }
      if (selected === null || selected === '' || selected.startsWith('group:')) {
        return;
      }
      const id = Number(selected);
      if (!Number.isNaN(id)) {
        this._value = id;
        params.api.stopEditing();
      }
    };
    const onClose = () => params.api.stopEditing();

    this._disposables.push(
      addEventListener(this._dropdown, 'searchChanged' as any, onSearch),
      addEventListener(this._dropdown, 'valueChanged' as any, onValueChanged),
      addEventListener(this._dropdown, 'selectOption' as any, onSelectOption),
      addEventListener(this._dropdown, 'close', onClose),
    );
  }

  private _filterRows(rows: ITaskTypePickerRow[], search: string): ITaskTypePickerRow[] {
    const filter = this._params.filterRows ?? filterTaskTypePickerRowsBySearch;
    return filter(rows, search);
  }

  private _renderOptions(): void {
    const rows = this._filterRows(this._params.getRows(), this._searchTerm);
    while (this._dropdown.firstChild) {
      this._dropdown.removeChild(this._dropdown.firstChild);
    }
    for (const row of rows) {
      const option = document.createElement('samc-option') as ISamcOptionElement;
      const isGroup = row.kind === 'group';
      option.value = isGroup ? `group:${row.key}` : String(row.id);
      option.text = row.text;
      option.hasChildren = isGroup;
      option.level = row.level;
      option.textContent = row.text;
      this._dropdown.appendChild(option);
    }
  }

  getGui(): HTMLElement {
    return this._gui;
  }

  getValue(): number | undefined | null {
    return this._value ?? undefined;
  }

  destroy(): void {
    for (let i = 0; i < this._disposables.length; i++)
      this._disposables[i].dispose();
    this._disposables = [];
  }
}
